/**
 * Local `stegaClean` — behaviour-identical to the one in `@sanity/client/stega`,
 * but without the import cost.
 *
 * That entry point does `export * from '@sanity/client'` and declares
 * `class SanityStegaClient extends SanityClient` at module top level, so no
 * bundler can shake the HTTP client out of it: a single `stegaClean` import
 * pulled `@sanity/client` + `get-it` + `rxjs` + the Buffer polyfill into the
 * browser bundle (28 KiB gzip, of which the browser never used a byte — it
 * issues no Sanity requests). The stripping itself needs nothing but a regex.
 *
 * Server code may import from `@sanity/client/stega` freely; the client is
 * already loaded there. Keep this util as the single source anyway, so no
 * caller has to know which graph it ends up in.
 */

/**
 * Code points Sanity encodes the document/field/path into (zero-width and
 * invisible-operator characters). Mirrors the upstream table.
 */
const STEGA_CODE_POINTS = [
    0x200b, 0x200c, 0x200d, 0x2062, 0x2063, 0x2060, 0xfeff, 0x2061, 0x1d173, 0x1d174, 0x1d175, 0x1d176, 0x1d177,
    0x1d178, 0x1d179, 0x1d17a,
];

// One payload is always ≥ 4 characters, so shorter runs are left alone.
const STEGA_PATTERN = new RegExp(
    `[${STEGA_CODE_POINTS.map((point) => `\\u{${point.toString(16)}}`).join('')}]{4,}`,
    'gu',
);

/**
 * Strips stega encoding from a string, or recursively from every string inside
 * an object/array. Falsy values pass through untouched — `stegaClean('')` stays
 * `''`, `stegaClean(undefined)` stays `undefined`.
 */
export function stegaClean<T>(value: T): T {
    return value ? (JSON.parse(JSON.stringify(value).replace(STEGA_PATTERN, '')) as T) : value;
}
